import type {
  IAffiliation,
  IArticle,
  IAuthor,
  IJournal,
  IResearchTopic,
  IRtSponsorship
} from "~/types";
import { useCacheService } from "./useCacheService";
import { SearchDataModel } from "~/types/models/SearchData.model";
import type { IArticleSchema } from "~/types/models/Article.model";
import { ArticleSortOptionEnum } from "~/enums/ArticleSortOptionEnum";
import type { Sort } from "mongodb";
import type { IAuthorSchema } from "~/types/models/Author.model";
import type {
  IResearchTopicBasicInfoSchema,
  IResearchTopicSchema
} from "~/types/models/ResearchTopic.model";
import {
  ResearchTopicSchema2Interface,
  RTSponsorshipSchema2Interface
} from "~/mappers";
import type { IRTSponsorshipSchema } from "~/types/models/RTSponsorship.model";
import type { IJournalSchema } from "~/types/models/Journal.model";
import type { IListResponse } from "~/types/IListResponse";
import { useMongoDbConnection } from "./useMongoDbConnection";
import type { IResearchTopicEbookSchema } from "~/types/models/ResearchTopicEbook.model";

export const useMongoDbService = () => {
  function getQueryFilters(rtId: number): object[] {
    const stageIds = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
    const filter: object[] = [
      { "ResearchTopic.ResearchTopicId": rtId },
      {
        PublishedDate: {
          $lt: new Date()
        }
      },
      { Authors: { $exists: true, $ne: [] } }
    ];

    if (excludedStageIds.length > 0) {
      filter.push({
        StageId: { $nin: excludedStageIds.split(",") }
      });
    }
    if (publiclyVisibleStages.length > 0) {
      filter.push({
        StageId: { $in: publiclyVisibleStages.split(",") }
      });
    }
    if (excludedJournalIds.length > 0) {
      filter.push({
        $or: [
          {
            "Journal.JournalId": {
              $nin: excludedJournalIds.split(",")
            }
          },
          { StageId: { $nin: stageIds } }
        ]
      });
    }
    return filter;
  }

  function mapToIArticle(article: IArticleSchema): IArticle {
    const impact = article.ImpactV5;
    const section = article.Section
      ? { sectionId: article.Section.SectionId }
      : undefined;
    return {
      id: article.ArticleId,
      doi: article.DOI,
      title: article.Title,
      articleType: article.ArticleType?.Name,
      publishedOn: article.PublishedDate && String(article.PublishedDate),
      recentDate: String(article.RecentDate),
      receivedDate: article.ReceivedDate,
      acceptedDate: article.AcceptedDate,
      isPublished: article.Published,
      authors: article.Authors?.map((author) => {
        return {
          userId: author.UserId,
          fullName: author.FullName
        };
      }),
      impactV5: {
        views: impact?.Views,
        downloads: impact?.Downloads,
        citations: impact?.Citations,
        viewsAndDownloads: impact?.ViewsAndDownloads
      },
      journal: {
        journalId: article.Journal.JournalId,
        journalSlug: article.Journal.JournalSlug
      },
      section: section,
      imageUrl: article.ImageUrl
    };
  }

  function getThumbnailUrl(userId: number): string {
    return `${loopUrl}/images/profile/${userId}/55`;
  }

  function getProfileUrl(userId: number): string {
    return `${loopUrl}/people/${userId}/overview`;
  }

  function getUniqueAuthors(authors: IAuthorSchema[]): IAuthorSchema[] {
    const uniqueIds = new Set();
    const uniqueEmails = new Set();

    const UniqueAuthors = authors.filter((record) => {
      if (record.UserId > 0) {
        if (!uniqueIds.has(record.UserId)) {
          uniqueIds.add(record.UserId);
          return true;
        }
      } else if (record.Email !== "") {
        if (!uniqueEmails.has(record.Email)) {
          uniqueEmails.add(record.Email);
          return true;
        }
      } else if (record.UserId === 0) {
        return true;
      }
      return false;
    });
    return UniqueAuthors;
  }

  function mapToAuthor(author: IAuthorSchema): IAuthor {
    return {
      userId: author.UserId,
      fullName: author.FullName
        ? author.FullName
        : [author.FirstName, author.MiddleName, author.LastName].join(" "),
      thumbnailUrl: getThumbnailUrl(author.UserId),
      profileUrl: getProfileUrl(author.UserId),
      affiliations: author.Affiliations?.map((Affiliation) => {
        return {
          department: Affiliation.Department,
          organization: Affiliation.Organization,
          city: Affiliation.City,
          country: Affiliation.Country,
          address: Affiliation.City
            ? Affiliation.City +
              (Affiliation.Country ? ", " + Affiliation.Country : "")
            : Affiliation.Country
            ? Affiliation.Country
            : ""
        } as IAffiliation;
      })
    };
  }

  function removeOfflineJournalsAndSectionsWithoutArticles(
    journals: IJournal[],
    allArticles: IArticle[]
  ): IJournal[] {
    return journals.filter((journal) => {
      if (journal.isPrimary) {
        if (journal.sections?.length) {
          journal.sections = journal.sections.filter((section) => {
            if (section.isOnline) return true;
            return allArticles.some(
              (article) =>
                article.journal.journalId === journal.id &&
                article.section?.sectionId === section.sectionId
            );
          });
        }
        return true;
      }

      if (journal.specialtyId) {
        if (journal.isOnline) return true;

        return allArticles.some(
          (article) => article.journal.journalId === journal.id
        );
      } else if (journal.sections?.length) {
        journal.sections = journal.sections.filter((section) => {
          if (section.isOnline) return true;

          return allArticles.some(
            (article) =>
              article.journal.journalId === journal.id &&
              article.section?.sectionId === section.sectionId
          );
        });

        if (journal.isOnline && journal.sections.length > 0) return true;

        return allArticles.some(
          (article) => article.journal.journalId === journal.id
        );
      }

      return false;
    });
  }

  function saveExistResearchTopicCache(
    researchTopicId: number,
    exists: boolean
  ): void {
    // These 2 lines will prevent the endpoints to check again if the rt exists
    const serverCacheKey = getFormerKey(
      "mongo",
      existRtKeyPrefix,
      researchTopicId
    );
    cache(serverCacheKey, () => exists, LONG_TTL);
  }

  async function getJournals(
    journalIds: number[],
    sectionIds: number[]
  ): Promise<IJournalSchema[]> {
    const cacheKey = getFormerKey("mongo", "get-journals", {
      journalIds,
      sectionIds
    });

    return cache(cacheKey, async () => {
      // const timeRTKey = `timer-get-journals-${journalIds.length}-${sectionIds.length}`;
      // console.time(timeRTKey);
      await connect();
      const filteredJournals = await database()
        .collection("journals")
        .aggregate([
          {
            $match: {
              JournalId: { $in: journalIds }
            }
          },
          {
            $project: {
              ArticleTypes: true,
              ImpactMetrics: true,
              JournalId: true,
              Online: true,
              OpenForSubmission: true,
              Slug: true,
              Title: true,
              Taxonomies: true,
              Sections: {
                $map: {
                  input: {
                    $filter: {
                      input: "$Sections",
                      as: "section",
                      cond: { $in: ["$$section.SectionId", sectionIds] }
                    }
                  },
                  as: "filteredSection",
                  in: {
                    SectionId: "$$filteredSection.SectionId",
                    Title: "$$filteredSection.Title",
                    Online: "$$filteredSection.Online",
                    Slug: "$$filteredSection.Slug",
                    OpenForSubmission: "$$filteredSection.OpenForSubmission",
                    ArticleTypes: "$$filteredSection.ArticleTypes",
                    Taxonomies: "$$filteredSection.Taxonomies"
                  }
                }
              }
            }
          }
        ])
        .toArray();
      // console.timeEnd(timeRTKey);
      return filteredJournals as IJournalSchema[];
    });
  }

  const existRtKeyPrefix = "exists-rt";
  const {
    loopUrl,
    excludedStageIds,
    publiclyVisibleStages,
    excludedJournalIds
  } = useRuntimeConfig().public;
  const { connect, database } = useMongoDbConnection();
  const { getFormerKey, cache, LONG_TTL, SHORT_TTL } = useCacheService();

  const existsResearchTopic = async (
    researchTopicId: number
  ): Promise<boolean> => {
    const serverCacheKey = getFormerKey(
      "mongo",
      existRtKeyPrefix,
      researchTopicId
    );

    return cache(serverCacheKey, async () => {
      const rt = getResearchTopicBasicInfo(researchTopicId);
      return rt !== null;
    });
  };

  const getArticles = async (
    researchTopicId: number,
    params: SearchDataModel = new SearchDataModel()
  ): Promise<IArticle[]> => {
    const cacheKey = getFormerKey(
      "mongo:get-articles",
      researchTopicId.toString(),
      params
    );

    return await cache(
      cacheKey,
      async () => {
        const projection = {
          "ArticleId": true,
          "Title": true,
          "DOI": true,
          "RecentDate": true,
          "PublishedDate": true,
          "AcceptedDate": true,
          "Published": true,
          "ArticleType": true,
          "ReceivedDate": true,
          "Authors.FullName": true,
          "ImpactV5": true,
          "Journal": true,
          "Section": true,
          "ImageUrl": true
        };
        const { limit, skip, sortby } = params;
        let mongoSortBy!: Sort;

        switch (sortby) {
          case ArticleSortOptionEnum.VIEWS:
            mongoSortBy = { "ImpactV5.Views": -1, "_id": 1 };
            break;
          case ArticleSortOptionEnum.ARTICLETYPE:
            mongoSortBy = { "ArticleType.Name": 1, "_id": 1 };
            break;
          case ArticleSortOptionEnum.DATE:
            mongoSortBy = { RecentDate: -1 };
            break;
          default:
            throw Error("Unhandled sort by param: " + sortby);
        }
        // const timeRTKey = `timer-get-articles-${researchTopicId}`;
        // console.time(timeRTKey);
        await connect();
        const articleListDocument = await database()
          .collection("articles")
          .find<IArticleSchema>(
            { "ResearchTopic.ResearchTopicId": researchTopicId },
            { projection }
          )
          .skip(skip)
          .limit(limit)
          .sort(mongoSortBy)
          .toArray();
        // console.timeEnd(timeRTKey);
        return articleListDocument.map((a) => mapToIArticle(a));
      },
      LONG_TTL
    );
  };

  const getAuthors = async (
    researchTopicId: number,
    params: SearchDataModel
  ): Promise<IListResponse<IAuthor>> => {
    const cacheKey = getFormerKey(
      "mongo:get-authors",
      researchTopicId.toString()
    );
    const allAuthors = await cache(
      cacheKey,
      async () => {
        let articleListDocument: IArticleSchema[] = [];
        const filter: object[] = getQueryFilters(researchTopicId);

        // const timeRTKey = `timer-get-authors-${researchTopicId}`;
        // console.time(timeRTKey);
        await connect();
        articleListDocument = await database()
          .collection("articles")
          .find<IArticleSchema>(
            { $and: filter },
            {
              projection: {
                "Authors.Affiliations.Department": true,
                "Authors.Affiliations.Organization": true,
                "Authors.Affiliations.City": true,
                "Authors.Affiliations.Country": true,
                "Authors.Email": true,
                "Authors.FirstName": true,
                "Authors.FullName": true,
                "Authors.LastName": true,
                "Authors.MiddleName": true,
                "Authors.ShortName": true,
                "Authors.UserId": true
              }
            }
          )
          .toArray();
        // console.timeEnd(timeRTKey);

        const authors: IAuthor[] = [];
        const users: IAuthorSchema[] = [];

        articleListDocument.map((article) => users.push(...article.Authors));
        const uniqueAuthors = getUniqueAuthors(users);
        authors.push(...uniqueAuthors.map((author) => mapToAuthor(author)));

        return authors;
      },
      LONG_TTL
    );

    const totalAuthors = allAuthors.length;
    let paginatedAndSortedAuthors: IAuthor[] = [];
    if (totalAuthors > 0) {
      paginatedAndSortedAuthors = allAuthors
        .sort((a, b) => {
          if (a.userId > 0 && b.userId > 0)
            return a.fullName.localeCompare(b.fullName);
          if (a.userId > 0) return -1;
          if (b.userId > 0) return 1;
          return a.fullName.localeCompare(b.fullName);
        })
        .slice(params.skip, params.skip + params.limit);
    }

    return {
      total: totalAuthors,
      items: paginatedAndSortedAuthors,
      offset: params.skip,
      limit: params.limit
    } as IListResponse<IAuthor>;
  };

  const getEbooks = async (
    researchTopicId: number
  ): Promise<IResearchTopicEbookSchema[]> => {
    const cacheKey = getFormerKey("mongo", "get-ebooks", researchTopicId);

    return cache(
      cacheKey,
      async () => {
        await connect();
        const ebooks = await database()
          .collection("research-topic-ebooks")
          .find<IResearchTopicEbookSchema>(
            { ResearchTopicId: researchTopicId },
            {
              projection: {
                ResearchTopicId: 1,
                EbookId: 1,
                FileServerVersionNumber: 1,
                FileServerId: 1,
                FileServerPackageEntryId: 1,
                FileName: 1,
                ContentTypeCode: 1,
                FileFormatDescription: 1
              }
            }
          )
          .toArray();

        return ebooks;
      },
      LONG_TTL
    );
  };

  const getResearchTopicBasicInfo = async (
    researchTopicId: number
  ): Promise<IResearchTopicBasicInfoSchema | null> => {
    const cacheKey = getFormerKey(
      "mongo",
      "get-rt-basic-info",
      researchTopicId
    );

    return cache(
      cacheKey,
      async () => {
        await connect();
        const rt = await database()
          .collection("research-topics")
          .findOne<IResearchTopicBasicInfoSchema>(
            { ResearchTopicId: researchTopicId },
            {
              projection: {
                ResearchTopicId: 1,
                Slug: 1,
                Title: 1,
                IsMagazinePage: 1
              }
            }
          );

        saveExistResearchTopicCache(researchTopicId, rt !== null);

        return rt;
      },
      LONG_TTL
    );
  };

  const getResearchTopicDetails = async (
    researchTopicId: number,
    tenantName: string
  ): Promise<IResearchTopic> => {
    const cacheKey = getFormerKey("mongo", "get-rt-details", researchTopicId);

    return await cache(
      cacheKey,
      async () => {
        // const timeRTKey = `timer-get-rt-${researchTopicId}`;
        // console.time(timeRTKey);
        await connect();
        const researchTopic = await database()
          .collection("research-topics")
          .findOne<IResearchTopicSchema>(
            { ResearchTopicId: researchTopicId },
            {
              projection: {
                "ResearchTopicId": 1,
                "Slug": 1,
                "Title": 1,
                "Description": 1,
                "Journals.IsPrimary": 1,
                "Journals.JournalId": 1,
                "Journals.Sections.SectionId": 1,
                "Journals.Title": 1,
                "Journals.SpecialtyId": 1,
                "Journals.DefaultDomainId": 1,
                "AbstractSubmissionDeadline": 1,
                "AuthorsCount": 1,
                "ClosedOn": 1,
                "OnlineSince": 1,
                "CreatedDate": 1,
                "ImageUrl": 1,
                "ImpactV5": 1,
                "IsOpenForManuscriptSubmission": 1,
                "IsOpenForSubmission": 1,
                "Keywords": 1,
                "ManuscriptSubmissionDeadline": 1,
                "ManuscriptSubmissionExtendedDeadline": 1,
                "StageId": 1,
                "TopicCoordinators": 1,
                "TopicEditors": 1
              }
            }
          );
        // console.timeEnd(timeRTKey);

        saveExistResearchTopicCache(researchTopicId, researchTopic !== null);

        if (!researchTopic) {
          throw Error(
            `Research Topic ID ${researchTopicId} not found in MongoDB`
          );
        }

        const sectionIds: number[] = [];
        researchTopic.Journals.forEach((m) => {
          if (!Array.isArray(m.Sections)) return;
          sectionIds.push(...m.Sections.map((s) => s.SectionId));
        });

        const allJournalsPromise = getJournals(
          researchTopic.Journals.map((j) => j.JournalId),
          sectionIds
        );
        const allArticlePromise = getArticles(researchTopicId);
        const ebookPromise =
          researchTopic.ClosedOn < new Date()
            ? getEbooks(researchTopicId)
            : Promise.resolve([]);

        const promises: [
          Promise<IJournalSchema[]>,
          Promise<IArticle[]>,
          Promise<IResearchTopicEbookSchema[]>
        ] = [allJournalsPromise, allArticlePromise, ebookPromise];

        const [allJournalsResponse, allArticlesResponse, ebooksResponse] =
          await Promise.all(promises);

        const allJournals: IJournalSchema[] = allJournalsResponse;
        const allArticles: IArticle[] = allArticlesResponse;
        const ebooks: string[] = ebooksResponse.map(
          (ebook) => ebook.FileFormatDescription
        );

        const journalsSchemas = allJournals.filter((j) =>
          (researchTopic?.Journals || [])
            .map((j) => j.JournalId)
            .includes(j.JournalId)
        );

        const researchTopicdata = ResearchTopicSchema2Interface(
          researchTopic,
          journalsSchemas,
          ebooks,
          tenantName
        );

        researchTopicdata.journals =
          removeOfflineJournalsAndSectionsWithoutArticles(
            researchTopicdata.journals,
            allArticles
          );

        return researchTopicdata;
      },
      SHORT_TTL
    );
  };

  const getResearchTopicSponsorshipDetails = async (
    researchTopicId: number
  ): Promise<IRtSponsorship> => {
    const cacheKey = getFormerKey(
      "mongo",
      "get-rt-sponsorhips",
      researchTopicId
    );
    return await cache(cacheKey, async () => {
      // const timeRTKey = `timer-get-sponsor-${researchTopicId}`;
      // console.time(timeRTKey);
      await connect();
      const sponsor = await database()
        .collection("research-topics-sponsorship")
        .findOne<IRTSponsorshipSchema>(
          { ResearchTopicId: Number(researchTopicId), IsDeleted: false },
          {
            projection: {
              Image: true,
              Message: true,
              Link: true,
              IsDeleted: true
            }
          }
        );
      // console.timeEnd(timeRTKey);
      return sponsor ? RTSponsorshipSchema2Interface(sponsor) : {};
    });
  };

  return {
    getArticles,
    getAuthors,
    getResearchTopicDetails,
    getResearchTopicSponsorshipDetails,
    getResearchTopicBasicInfo,
    existsResearchTopic
  };
};
